import * as React from 'react'
import { ReactElement, useState } from 'react'
import {
    Button,
    ButtonProps,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    DialogTrigger,
    Field,
    Tooltip,
} from '@fluentui/react-components'
import {
    bundleIcon,
    CheckmarkFilled,
    CheckmarkRegular,
    DismissFilled,
    DismissRegular,
    Notepad16Filled,
    Notepad16Regular,
    NotepadEdit16Filled,
    NotepadEdit16Regular,
} from '@fluentui/react-icons'
import { useBoolean } from '@fluentui/react-hooks'
import { InvoiceOverviewDto } from '../../../config/finance/invoiceConfig'

import Textarea from '../../Text/TextArea'
import useAxios from '../../../hooks/useAxios'
import { useTranslation } from 'react-i18next'

// @ts-expect-error("dompurify exports differ from what ts-lint is expecting and cannot see the default export")
import DOMPurify from 'dompurify'

type Props = {
    invoice: InvoiceOverviewDto
    onSubmit: (value: string) => void
    buttonProps?: ButtonProps
}

const CheckmarkIcon = bundleIcon(CheckmarkFilled, CheckmarkRegular)
const DismissIcon = bundleIcon(DismissFilled, DismissRegular)

const AddIcon = bundleIcon(Notepad16Filled, Notepad16Regular)
const EditIcon = bundleIcon(NotepadEdit16Filled, NotepadEdit16Regular)

export default function InvoiceNoteDialog(props: Props): ReactElement {
    const axios = useAxios()
    const { t: translate } = useTranslation()

    const [submitting, { setTrue: setIsSubmitting, setFalse: setNotSubmitting }] = useBoolean(false)
    const [value, setValue] = useState<string>(props.invoice.note ?? '')

    return (
        <Dialog {...props}>
            <DialogTrigger disableButtonEnhancement>
                <Tooltip
                    content={
                        props.invoice.note ? (
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: DOMPurify.sanitize(props.invoice.note)?.replaceAll('\n', '<br/>'),
                                }}
                            />
                        ) : (
                            translate('invoice.table.note.add')
                        )
                    }
                    relationship={'label'}
                >
                    <Button
                        icon={!!props.invoice.note ? <EditIcon /> : <AddIcon />}
                        appearance={'transparent'}
                        size={'small'}
                        {...props.buttonProps}
                    />
                </Tooltip>
            </DialogTrigger>

            <DialogSurface>
                <DialogBody>
                    <DialogTitle>{props.invoice.note ? translate('invoice.table.note.edit') : translate('invoice.table.note.add')}</DialogTitle>
                    <DialogContent>
                        <Field label={translate('invoice.table.header.note')}>
                            <Textarea value={value} autoAdjustHeight onChange={(_, data) => setValue(data.value)} />
                        </Field>
                    </DialogContent>
                    <DialogActions>
                        <DialogTrigger disableButtonEnhancement>
                            <Button appearance="transparent" icon={<DismissIcon />}>
                                {translate('action.cancel')}
                            </Button>
                        </DialogTrigger>
                        <DialogTrigger disableButtonEnhancement>
                            <Button
                                appearance="primary"
                                disabled={submitting}
                                icon={<CheckmarkIcon />}
                                onClick={() => {
                                    setIsSubmitting()
                                    axios
                                        .put(`/finance/invoice/${props.invoice.id}/note`, { value: value })
                                        .then(() => props.onSubmit(value))
                                        .finally(() => setNotSubmitting())
                                }}
                            >
                                {translate('action.save')}
                            </Button>
                        </DialogTrigger>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    )
}
